'use client';

import type React from 'react';
import { Button } from '@zillow/constellation-10';
import type { ListingCTA } from '@/src/components/features/property/hooks/useListingCTAs';

type ListingCTAButtonProps = Omit<
  React.ComponentProps<typeof Button.Root>,
  'asChild' | 'children' | 'emphasis' | 'onClick' | 'tone'
> & {
  cta: ListingCTA;
  labelClassName?: string;
};

/** An outbound CTA renders as a link that opens in a new tab. */
export const ListingCTAButton = ({ cta, labelClassName, ...rootProps }: ListingCTAButtonProps) => {
  const content = (
    <>
      {cta.icon ? <Button.Icon>{cta.icon}</Button.Icon> : null}
      <Button.Label className={labelClassName}>{cta.label}</Button.Label>
    </>
  );

  return (
    <Button.Root
      type="button"
      {...rootProps}
      tone="brand"
      emphasis={cta.emphasis}
      asChild={Boolean(cta.href)}
      onClick={cta.href ? undefined : cta.onClick}
      disabled={cta.disabled}
      aria-busy={cta.ariaBusy}
      aria-label={cta.ariaLabel}
    >
      {cta.href ? (
        <a
          href={cta.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={cta.onClick}
          onAuxClick={(event) => event.button === 1 && cta.onClick(event)}
        >
          {content}
        </a>
      ) : (
        content
      )}
    </Button.Root>
  );
};
